import React from "react";
import { View, Linking } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import { StepShell } from "../components/StepShell";
import { FileUpload } from "@/components/ui/FileUpload";
import { Tag } from "@/components/ui/Tag";
import { Checkbox } from "@/components/ui/Checkbox";
import { Label } from "@/components/ui/Label";
import { MultiSelectPickerModal } from "@/components/ui/MultiSelectPickerModal";
import { usePreVettingStep3Form } from "../hooks/usePreVettingStep3Form";
import type { TranslationKeys } from "@chinese-vendor/i18n";

export default function PreVettingStep3Screen() {
  const { t } = useI18n();
  const {
    form,
    onFieldBlur,
    handleNext,
    handleBack,
    declarationChecked,
    setDeclarationChecked,
    declarationError,
    setDeclarationError,
    isoCertFile,
    isoCertUploadState,
    isoCertUploadError,
    handleIsoCertFile,
    handleIsoCertRemove,
    ceMarkingFile,
    ceMarkingUploadState,
    ceMarkingUploadError,
    handleCeMarkingFile,
    handleCeMarkingRemove,
    qualityReportFile,
    qualityReportUploadState,
    qualityReportUploadError,
    handleQualityReportFile,
    handleQualityReportRemove,
    ecoCertFile,
    ecoCertUploadState,
    ecoCertUploadError,
    handleEcoCertFile,
    handleEcoCertRemove,
    industryPickerVisible,
    openIndustryPicker,
    closeIndustryPicker,
    subcategoryPickerVisible,
    openSubcategoryPicker,
    closeSubcategoryPicker,
    allIndustries,
    industriesLoading,
    allSubcategories,
    subcategoriesLoading,
    saveIndustries,
    saveSubcategories,
    pickerLocale,
    togglePickerLocale,
  } = usePreVettingStep3Form();

  const { errors } = form.formState;

  return (
    <StepShell
      step={3}
      onNext={handleNext}
      onBack={handleBack}
      nextLabel="preVetting.step3.nextLabel"
      iconName="clipboard-check"
      title="preVetting.step3.title"
      subtitle="preVetting.step3.subtitle"
    >
      <View className="items-start">
        <View className="mb-8">
          <Label required label="preVetting.step3.industries" />
          <View className="flex-row flex-wrap gap-2 mb-1">
            {form.watch("industry").map((id) => {
              const industry = allIndustries.find((i) => i.id === id);
              return industry ? (
                <Tag
                  key={id}
                  variant="selected"
                  label="preVetting.step3.industries"
                  rawLabel={industry.name}
                  onRemove={() =>
                    saveIndustries(form.getValues("industry").filter((v) => v !== id))
                  }
                />
              ) : null;
            })}
            <Tag variant="add" label="preVetting.step3.addIndustry" onPress={openIndustryPicker} />
          </View>
          {errors.industry && (
            <AppText role="alert" className="text-xs text-error mb-3">
              {t(errors.industry.message as TranslationKeys)}
            </AppText>
          )}
        </View>

        <View>
          <Label required label="preVetting.step3.productSubcategories" />
          <View className="flex-row flex-wrap gap-2 mb-1">
            {form.watch("product_subcategories").map((id) => {
              const subcategory = allSubcategories.find((s) => s.id === id);
              return subcategory ? (
                <Tag
                  key={id}
                  variant="selected"
                  label="preVetting.step3.productSubcategories"
                  rawLabel={subcategory.name}
                  onRemove={() =>
                    saveSubcategories(
                      form.getValues("product_subcategories").filter((v) => v !== id)
                    )
                  }
                />
              ) : null;
            })}
            <Tag
              variant="add"
              label="preVetting.step3.addCategory"
              onPress={openSubcategoryPicker}
            />
          </View>
          {errors.product_subcategories && (
            <AppText role="alert" className="text-xs text-error mb-3">
              {t(errors.product_subcategories.message as TranslationKeys)}
            </AppText>
          )}
        </View>

        <View className="h-[1px] bg-stroke-50 my-8 w-full" />

        {/* ─── Compliance & Certifications section ──────────────────────── */}
        <AppText
          weight="bold"
          className="text-xs text-dark-text-15 uppercase tracking-widest mt-2 mb-4"
        >
          {t("preVetting.step3.sectionCompliance")}
        </AppText>
        <View className="tablet:flex-row tablet:gap-4 w-full">
          <View className="flex-1">
            <FileUpload
              label="preVetting.step3.isoCertificationOptional"
              hint="preVetting.step3.isoCertHint"
              onFile={handleIsoCertFile}
              uploadState={isoCertUploadState}
              currentFile={isoCertFile}
              onViewFile={isoCertFile ? () => Linking.openURL(isoCertFile.uri) : undefined}
              onRemoveFile={handleIsoCertRemove}
              error={isoCertUploadError}
            />
          </View>

          <View className="flex-1">
            <FileUpload
              label="preVetting.step3.ceMarkingOptional"
              hint="preVetting.step3.ceMarkingHint"
              onFile={handleCeMarkingFile}
              uploadState={ceMarkingUploadState}
              currentFile={ceMarkingFile}
              onViewFile={ceMarkingFile ? () => Linking.openURL(ceMarkingFile.uri) : undefined}
              onRemoveFile={handleCeMarkingRemove}
              error={ceMarkingUploadError}
            />
          </View>
        </View>
        <View className="tablet:flex-row tablet:gap-4 w-full">
          <View className="flex-1">
            <FileUpload
              label="preVetting.step3.qualityTestReportOptional"
              hint="preVetting.step3.qualityReportHint"
              onFile={handleQualityReportFile}
              uploadState={qualityReportUploadState}
              currentFile={qualityReportFile}
              onViewFile={
                qualityReportFile ? () => Linking.openURL(qualityReportFile.uri) : undefined
              }
              onRemoveFile={handleQualityReportRemove}
              error={qualityReportUploadError}
            />
          </View>

          <View className="flex-1">
            <FileUpload
              label="preVetting.step3.ecoCertificationOptional"
              hint="preVetting.step3.ecoCertHint"
              onFile={handleEcoCertFile}
              uploadState={ecoCertUploadState}
              currentFile={ecoCertFile}
              onViewFile={ecoCertFile ? () => Linking.openURL(ecoCertFile.uri) : undefined}
              onRemoveFile={handleEcoCertRemove}
              error={ecoCertUploadError}
            />
          </View>
        </View>

        {/* ─── Declaration checkbox ────────────────────────────────── */}
        <View
          className={`my-4 overflow-hidden border rounded-lg w-full p-2 ${declarationError ? "bg-error-light border-error" : "bg-error-light border-error-border"}`}
        >
          <Checkbox
            checked={declarationChecked}
            onChange={(checked) => {
              setDeclarationChecked(checked);
              if (checked) setDeclarationError(false);
            }}
            label="preVetting.step3.declaration"
            required
          />
        </View>
        {declarationError && (
          <AppText role="alert" className="text-xs text-error -mt-3 mb-2">
            {t("common.required")}
          </AppText>
        )}
      </View>

      {/* ─── Industry picker modal ───────────────────────────────────── */}
      <MultiSelectPickerModal
        visible={industryPickerVisible}
        onDismiss={closeIndustryPicker}
        title="preVetting.step3.addIndustry"
        subtitle="preVetting.step3.industries"
        allSectionLabel="multiSelectPicker.allIndustries"
        selectedSectionLabel="multiSelectPicker.selectedIndustries"
        selectedCountKey="multiSelectPicker.selectedIndustriesCount"
        searchPlaceholder="multiSelectPicker.searchIndustries"
        items={allIndustries}
        selectedIds={form.watch("industry")}
        onSave={saveIndustries}
        isLoading={industriesLoading}
        onLocaleToggle={togglePickerLocale}
        localeToggleLabel={
          pickerLocale === "zh-CN"
            ? "multiSelectPicker.switchToEnglish"
            : "multiSelectPicker.switchToChinese"
        }
      />

      {/* ─── Product category picker modal ──────────────────────────── */}
      <MultiSelectPickerModal
        visible={subcategoryPickerVisible}
        onDismiss={closeSubcategoryPicker}
        title="preVetting.step3.addCategory"
        subtitle="preVetting.step3.productSubcategories"
        allSectionLabel="multiSelectPicker.allCategories"
        selectedSectionLabel="multiSelectPicker.selectedCategories"
        selectedCountKey="multiSelectPicker.selectedCategoriesCount"
        searchPlaceholder="multiSelectPicker.searchCategories"
        items={allSubcategories}
        selectedIds={form.watch("product_subcategories")}
        onSave={saveSubcategories}
        isLoading={subcategoriesLoading}
        onLocaleToggle={togglePickerLocale}
        localeToggleLabel={
          pickerLocale === "zh-CN"
            ? "multiSelectPicker.switchToEnglish"
            : "multiSelectPicker.switchToChinese"
        }
      />
    </StepShell>
  );
}
